import type { PropsWithChildren } from "react";
import { createContext, useContext, useEffect, useState } from "react";

const TimeContext = createContext<Date | null>(null);

export const useCurrentTime = (override?: Date) => {
  const context = useContext(TimeContext);

  if (override) {
    return override;
  }

  if (context === null) {
    throw new Error("useCurrentTime must be used within a TimeProvider");
  }

  return context;
};

/**
 * Returns the current timestamp, but with seconds and milliseconds set to 0, meaning it is at minute resolution.
 *
 * @param prev - optional previous value that is returned if it is the same time as the new "now".
 */
const now = (prev?: Date) => {
  // Now, but at minute resolution.
  const next = new Date();
  next.setSeconds(0);
  next.setMilliseconds(0);

  // If prev is provided, and it is the same time as next, return prev.
  // This will help React avoid re-rendering if the time hasn't changed.
  return next.getTime() === prev?.getTime() ? prev : next;
};

/**
 * Provides the current time to its children.
 *
 * It checks the current time every second, but the output is only updated every minute.
 *
 * This means that it is very efficient and sufficiently precise, but only to minute resolution.
 */
export const CurrentTimeProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [currentTime, setCurrentTime] = useState(now);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(now);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <TimeContext.Provider value={currentTime}>{children}</TimeContext.Provider>
  );
};
